import { MenuItemOption, MenuList, MenuOptionGroup, Portal } from "@chakra-ui/react";
import { ReactNode, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";

import { SIZES } from "../../theme/consts/sizing-consts";

import { RanaSelectValueType } from "./rana-select";
import { RanaSelectOptionLabel } from "./rana-select-option-label";

export function RanaSelectMenuList<Value extends RanaSelectValueType>({
  minW,
  value,
  selectedIdx,
  searchString,
  options,
  onChange,
  increaseMaxItems,
  initialMaxItems,
  maxItems,
}: {
  minW: string;
  value: Value | undefined;
  selectedIdx: number;
  searchString: string;
  options: {
    label: ReactNode | ((searchString: string) => ReactNode);
    value: Value;
    searchBy?: string;
  }[];
  onChange: (value: Value) => void;
  increaseMaxItems: () => void;
  initialMaxItems: number;
  maxItems: number;
}) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const selectedItemRef = useRef<HTMLButtonElement | null>(null);

  // Scroll to top when search string changes
  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [searchString]);

  // Make sure the selected item is visible when navigating with keyboard
  useEffect(() => {
    if (selectedItemRef.current && listRef.current) {
      const itemRect = selectedItemRef.current.getBoundingClientRect();
      const listRect = listRef.current.getBoundingClientRect();

      // Check if item is out of viewport
      if (itemRect.top < listRect.top || itemRect.bottom > listRect.bottom) {
        selectedItemRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedItemRef, listRef, selectedIdx]);

  // Increase max items when scrolling to bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const isAtBottom = Math.ceil(target.scrollHeight - target.scrollTop) === target.clientHeight;

    if (!isAtBottom) return;
    increaseMaxItems();
  };

  // Hide scrollbar if there are fewer or same amount of items than the max visible height
  const hideScrollbar = options.length <= initialMaxItems;

  return (
    <Portal>
      <MenuList
        maxH={`calc(${initialMaxItems - 0.5} * ${SIZES["button"]})`}
        overflowY="auto"
        overscrollBehavior="contain"
        ref={listRef}
        onScroll={handleScroll}
        minW={minW}
        sx={
          hideScrollbar
            ? {
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }
            : undefined
        }
      >
        <AnimateSize animateWidth whiteSpace="nowrap">
          <MenuOptionGroup value={value + ""} type="radio">
            {options.length ? (
              options
                .filter((_, idx) => idx < maxItems)
                .map(({ value, label }, idx) => (
                  <MenuItemOption
                    minW={minW}
                    key={value}
                    ref={idx === selectedIdx ? selectedItemRef : null}
                    value={value + ""}
                    bg={idx === selectedIdx ? "button.tertiary.hover" : undefined}
                    onClick={() => {
                      onChange(value);
                    }}
                  >
                    <RanaSelectOptionLabel searchString={searchString} content={label} />
                  </MenuItemOption>
                ))
            ) : (
              <MenuItemOption minW={minW} icon={null}>
                <FormattedMessage
                  id="dropdown.no_items"
                  defaultMessage="No items found with this search"
                />
              </MenuItemOption>
            )}
          </MenuOptionGroup>
        </AnimateSize>
      </MenuList>
    </Portal>
  );
}
